import type {
  ReviewBlock,
  ReviewOutlineNode,
} from '@/modules/templates/components/review/adapter/types';
import { buildOutline, type OutlineSection } from './docx-block-model';

/** Number of heading groups expanded by default. */
const DEFAULT_OPEN_COUNT = 6;

/**
 * A lightweight reference to a paragraph in the rendered document.
 *
 * Outline entries deliberately carry only an id, not a resolved `DocxBlockInfo`:
 * describing a block walks the whole body, so building the outline eagerly with
 * full info would be O(n²). The id is resolved to a live block at click time.
 */
export interface DocxBlockRef {
  kind: 'docx-paragraph';
  id: string;
}

export const isDocxBlockRef = (source: unknown): source is DocxBlockRef =>
  typeof source === 'object' &&
  source !== null &&
  (source as DocxBlockRef).kind === 'docx-paragraph';

const leaf = (id: string, text: string): ReviewOutlineNode => ({
  id,
  kind: 'block',
  title: text,
  block: {
    id,
    text,
    kind: 'paragraph',
    isTitle: false,
    source: { kind: 'docx-paragraph', id } satisfies DocxBlockRef,
  } satisfies ReviewBlock,
});

/**
 * Build the shared outline shape from a rendered .docx.
 *
 * Mirrors the `LegalDocument` builder so one `OutlineTree` renders both: a
 * lead-in section (text before the first heading) contributes root-level
 * leaves, and every heading becomes a collapsible group.
 */
export function buildDocxOutline(root: HTMLElement | null): ReviewOutlineNode[] {
  if (!root) return [];
  const sections: OutlineSection[] = buildOutline(root);
  const nodes: ReviewOutlineNode[] = [];
  let groupIndex = 0;

  sections.forEach((section) => {
    if (!section.headingId) {
      // Content before the first heading — no group to hang it under.
      nodes.push(...section.items.map((item) => leaf(item.id, item.text)));
      return;
    }

    const children = [
      leaf(section.headingId, section.title),
      ...section.items.map((item) => leaf(item.id, item.text)),
    ];

    nodes.push({
      id: `section:${section.headingId}`,
      kind: 'group',
      variant: 'section',
      title: section.title,
      defaultOpen: groupIndex < DEFAULT_OPEN_COUNT,
      children,
      blockIds: children.map((child) => child.id),
    });
    groupIndex += 1;
  });

  return nodes;
}
